"use client";

import {
  Badge,
  PopoverContent,
  PopoverRoot,
  PopoverTrigger,
} from "@elion-health/ui";
import type { ReactNode } from "react";
import { CoverageSwatch } from "./CoverageLegend";
import { CoverageRing } from "./CoverageRing";
import {
  COVERAGE_LABEL,
  COVERAGE_STATES,
  type CoverageCounts,
  coverageTotal,
  describeCoverage,
} from "./coverage";
import { HoverPopover, type HoverPopoverLink } from "./HoverPopover";
import type { MapLinkComponent } from "./link";

/** How many workflows a tile lists before collapsing the rest. */
const WORKFLOW_LIMIT = 5;
/** How many applications the applications panel names. */
const APPLICATION_LIMIT = 8;

export interface FunctionTileWorkflow {
  id: string;
  name: string;
}

export interface FunctionTileApplication {
  id: string;
  name: string;
}

export interface FunctionTileProps {
  name: string;
  description?: string | null;
  /** The function's page; the whole tile links there. */
  href: string;
  coverage: CoverageCounts;
  applicationCount: number;
  productCount: number;
  /** The applications mapped to its steps, named in the applications panel. */
  applications?: FunctionTileApplication[];
  /** Makes the applications badge a link, e.g. to the inventory filtered here. */
  applicationsHref?: string;
  /** What the applications panel says the badge opens. */
  applicationsCta?: string;
  workflows: FunctionTileWorkflow[];
  linkComponent?: MapLinkComponent;
  /** For the applications badge link; anchor props pass through to it. */
  badgeLinkComponent?: HoverPopoverLink;
}

export function plural(count: number, noun: string) {
  return `${count} ${noun}${count === 1 ? "" : "s"}`;
}

// The badges sit above the tile's stretched link (z-10) so their hover lands.
const BADGE_TRIGGER = "relative z-10 rounded-full";
// The DS badge, restyled to the map's quieter outlined pill.
const BADGE_CLASS =
  "border border-(--border-subtle) bg-(--bg-white) px-2 py-1 text-[11px] font-medium tabular-nums text-(--fg-secondary) dark:bg-(--bg-white) dark:text-(--fg-secondary)";

function PanelHeading({ children }: { children: ReactNode }) {
  return (
    <p className="mb-1.5! text-[10.5px] font-semibold text-(--fg-muted)!">
      {children}
    </p>
  );
}

/**
 * A business function at a glance: its coverage ring and step count, the
 * applications mapped to it, and the workflows inside it. The whole tile
 * opens the function; hovering a badge breaks it down.
 */
export function FunctionTile({
  name,
  description,
  href,
  coverage,
  applicationCount,
  productCount,
  applications = [],
  applicationsHref,
  applicationsCta = "Open in inventory",
  workflows,
  linkComponent: Link = "a",
  badgeLinkComponent,
}: FunctionTileProps) {
  const steps = coverageTotal(coverage);
  const shown = workflows.slice(0, WORKFLOW_LIMIT);
  const hidden = workflows.slice(WORKFLOW_LIMIT);
  const named = applications.slice(0, APPLICATION_LIMIT);
  const unnamed = applicationCount - named.length;
  // The panel is hidden from assistive tech, so the label carries the names.
  const names = named.map((app) => app.name).join(", ");
  const applicationsLabel = `${plural(applicationCount, "application")} and ${plural(productCount, "product")} mapped to ${name}${
    names ? `: ${names}` : ""
  }${unnamed > 0 ? `, ${unnamed} more` : ""}`;
  return (
    <article className="relative flex h-full flex-col rounded-(--radius-field) border border-(--border-subtle) bg-(--bg-canvas) p-4 transition-colors focus-within:border-(--border-strong) hover:border-(--border-strong)">
      <h3 className="text-[13.5px]! leading-snug font-semibold">
        {/* Stretched link: the whole tile opens the function. */}
        <Link
          href={href}
          className="no-underline! text-inherit! after:absolute after:inset-0 after:rounded-(--radius-field) focus-visible:outline-none focus-visible:after:outline-2 focus-visible:after:outline-offset-2 focus-visible:after:outline-(--fg-primary)"
        >
          {name}
        </Link>
      </h3>
      {description && (
        <p className="mt-1! line-clamp-2 text-[11.5px] leading-relaxed text-(--fg-muted)!">
          {description}
        </p>
      )}
      <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
        <HoverPopover
          label={`Step coverage: ${describeCoverage(coverage)}`}
          triggerClassName={BADGE_TRIGGER}
          className="w-[236px] p-3"
          trigger={
            <Badge
              size="lg"
              icon={<CoverageRing coverage={coverage} size={14} />}
              text={plural(steps, "step")}
              className={`${BADGE_CLASS} gap-1.5 pl-3.5`}
            />
          }
        >
          <PanelHeading>{plural(steps, "step")}</PanelHeading>
          <ul className="flex flex-col gap-1">
            {COVERAGE_STATES.map((state) => (
              <li
                key={state}
                className="flex items-center gap-2 text-[11.5px] text-(--fg-secondary)"
              >
                <CoverageSwatch state={state} />
                <span className="flex-1">{COVERAGE_LABEL[state]}</span>
                <span className="font-semibold tabular-nums text-(--fg-primary)">
                  {coverage[state]}
                </span>
              </li>
            ))}
          </ul>
        </HoverPopover>
        <HoverPopover
          label={
            applicationsHref
              ? `${applicationsLabel}. ${applicationsCta}`
              : applicationsLabel
          }
          href={applicationsHref}
          linkComponent={badgeLinkComponent}
          triggerClassName={`${BADGE_TRIGGER} ${
            applicationsHref
              ? "[&>*]:transition-colors hover:[&>*]:border-(--border-strong) hover:[&>*]:text-(--fg-primary)"
              : ""
          }`}
          className="w-[240px] p-3"
          trigger={
            <Badge
              text={plural(applicationCount, "application")}
              className={BADGE_CLASS}
            />
          }
        >
          <PanelHeading>
            {plural(applicationCount, "application")} in this function
          </PanelHeading>
          {applicationCount > 0 ? (
            <ul className="flex flex-col gap-1">
              {named.map((app) => (
                <li
                  key={app.id}
                  className="truncate text-[11.5px] text-(--fg-primary)"
                >
                  {app.name}
                </li>
              ))}
              {unnamed > 0 && (
                <li className="text-[11px] text-(--fg-muted)">
                  +{unnamed} more
                </li>
              )}
            </ul>
          ) : (
            <p className="text-[11.5px] text-(--fg-muted)!">
              No application mapped yet.
            </p>
          )}
          {productCount > 0 && (
            <p className="mt-2! border-t border-(--border-subtle) pt-1.5 text-[10.5px] text-(--fg-muted)!">
              {plural(productCount, "catalog product")}
            </p>
          )}
          {applicationsHref && (
            <p className="mt-2! text-[10.5px] font-medium text-(--fg-brand)!">
              {applicationsCta} →
            </p>
          )}
        </HoverPopover>
      </div>
      <ul className="mt-3 flex flex-1 flex-col gap-0.5 border-t border-(--border-subtle) pt-2.5">
        {shown.map((workflow) => (
          <li
            key={workflow.id}
            className="truncate text-[11.5px] text-(--fg-secondary)"
          >
            {workflow.name}
          </li>
        ))}
        {hidden.length > 0 && (
          <li>
            <MoreWorkflows workflows={hidden} />
          </li>
        )}
      </ul>
    </article>
  );
}

function MoreWorkflows({ workflows }: { workflows: FunctionTileWorkflow[] }) {
  return (
    <PopoverRoot>
      {/* Above the stretched link so it opens the list instead of navigating. */}
      <PopoverTrigger className="relative z-10 cursor-pointer rounded-(--radius-xs) text-[11px] text-(--fg-muted) hover:text-(--fg-primary) focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--fg-primary)">
        +{plural(workflows.length, "more workflow")}
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-3">
        <ul className="flex flex-col gap-1">
          {workflows.map((workflow) => (
            <li
              key={workflow.id}
              className="text-[11.5px] text-(--fg-secondary)"
            >
              {workflow.name}
            </li>
          ))}
        </ul>
      </PopoverContent>
    </PopoverRoot>
  );
}
